import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiInfo, FiMessageSquare, FiTag, FiClock, FiFileText, FiList, FiGitCommit, FiChevronUp, FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { ArtifactoryMarkdown } from './ArtifactoryMarkdown';
import { useArtifactoryStore } from '../../stores/artifactoryStore';
import { ExportButton } from './ExportButton';
import { HandoffButton } from './HandoffButton';
import { HydrationPlanPanel } from './HydrationPlanPanel';
import { useNavigate } from 'react-router-dom';

interface IntentHistoryEntry {
  id: string;
  content: string;
  timestamp: string;
  mutation_reason?: string;
  user_prompt: string;
  affected_nodes?: string[];
}

interface ProjectDetailsPanelProps {
  intent: string;
  originalIntent?: string;
  projectPlan?: string;
  manifestId: string | null;
  sessionId: string | null;
  createdAt?: string;
  updatedAt?: string;
  tags?: string[];
  intentHistory?: IntentHistoryEntry[];
  manifest?: ProjectManifest | null; // Add this prop
  onIntentUpdate?: (newIntent: string) => Promise<void>;
}

export const ProjectDetailsPanel: React.FC<ProjectDetailsPanelProps> = ({
  intent,
  originalIntent,
  projectPlan,
  manifestId,
  sessionId,
  createdAt,
  updatedAt,
  tags = [],
  intentHistory = [],
  manifest = null, // Add this prop with default value
  onIntentUpdate
}) => {
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'plan' | 'intent' | 'history' | 'details' | 'hydration'>('plan');
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Handle tab switching with toggle behavior
  const handleTabSwitch = (tab: 'plan' | 'intent' | 'history' | 'details') => {
    if (activeTab === tab && !isCollapsed) {
      // If clicking on the already active tab and panel is expanded, collapse it
      setIsCollapsed(true);
    } else if (isCollapsed) {
      // If panel is collapsed, expand it and set the active tab
      setIsCollapsed(false);
      setActiveTab(tab);
    } else {
      // Otherwise, just switch tabs
      setActiveTab(tab);
    }
  };
  
  // If intent is empty but originalIntent exists, use originalIntent
  const displayIntent = intent?.trim() ? intent : originalIntent || "No intent specified";
  
  // Generate a default project plan if none is provided
  const displayPlan = projectPlan?.trim() ? projectPlan : `This project is based on the following intent:
  
${displayIntent}

The system has automatically generated a file structure to implement this project.
You can modify the project by updating the intent through the chat interface.`;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get project name and history from store - moved outside conditional to follow React hooks rules
  const { projectName, projectNameHistory } = useArtifactoryStore();
  
  // Create content for collapsed state
  const renderCollapsedContent = () => (
    <div className={`mb-4 rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Tab Navigation - still visible when collapsed */}
      <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'plan'
              ? darkMode
                ? 'bg-green-900 text-green-300 border-b-2 border-green-500'
                : 'bg-green-50 text-green-700 border-b-2 border-green-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('plan')}
        >
          <FiFileText className="mr-2" />
          Project Plan
        </button>
          
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'intent'
              ? darkMode
                ? 'bg-blue-900 text-blue-300 border-b-2 border-blue-500'
                : 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('intent')}
        >
          <FiMessageSquare className="mr-2" />
          Current Intent
        </button>
          
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'history'
              ? darkMode
                ? 'bg-purple-900 text-purple-300 border-b-2 border-purple-500'
                : 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('history')}
        >
          <FiGitCommit className="mr-2" />
          Intent History
        </button>
          
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'details'
              ? darkMode
                ? 'bg-blue-900 text-blue-300 border-b-2 border-blue-500'
                : 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('details')}
        >
          <FiInfo className="mr-2" />
          Project Details
        </button>
          
        
        {/* Add expand button on the right */}
        <div className="ml-auto">
          <button
            className={`px-3 py-2 flex items-center ${
              darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed(false);
            }}
            title="Expand panel"
          >
            <FiChevronDown />
          </button>
        </div>
      </div>
      
      {/* Project Name Display - new addition */}
      <div className={`px-4 py-2 ${darkMode ? 'bg-gray-750' : 'bg-gray-50'} text-center`}>
        <span className={`font-mono text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
          {projectName}
        </span>
      </div>
    </div>
  );
  
  // If collapsed, return the collapsed content
  if (isCollapsed) {
    return renderCollapsedContent();
  }

  return (
    <div className={`mb-4 rounded-lg shadow-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Tab Navigation with Collapse Button */}
      <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'plan'
              ? darkMode
                ? 'bg-green-900 text-green-300 border-b-2 border-green-500'
                : 'bg-green-50 text-green-700 border-b-2 border-green-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('plan')}
        >
          <FiFileText className="mr-2" />
          Project Plan
        </button>
          
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'intent'
              ? darkMode
                ? 'bg-blue-900 text-blue-300 border-b-2 border-blue-500'
                : 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('intent')}
        >
          <FiMessageSquare className="mr-2" />
          Current Intent
        </button>
          
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'history'
              ? darkMode
                ? 'bg-purple-900 text-purple-300 border-b-2 border-purple-500'
                : 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('history')}
        >
          <FiGitCommit className="mr-2" />
          Intent History
        </button>
          
        <button
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'details'
              ? darkMode
                ? 'bg-blue-900 text-blue-300 border-b-2 border-blue-500'
                : 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
              : darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          onClick={() => handleTabSwitch('details')}
        >
          <FiInfo className="mr-2" />
          Project Details
        </button>
        
        {/* Add collapse button on the right */}
        <div className="ml-auto">
          <button
            className={`px-3 py-2 flex items-center ${
              darkMode
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed(true);
            }}
            title="Collapse panel"
          >
            <FiChevronUp />
          </button>
        </div>
      </div>

      {/* Tab Content - with max height and scrolling */}
      <div className="p-4 max-h-[300px] overflow-y-auto">
        {activeTab === 'plan' ? (
          <div>
            <h3 className="text-lg font-medium mb-2">Project Plan</h3>
            <div className={`p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <ArtifactoryMarkdown content={displayPlan} />
            </div>
          </div>
        ) : activeTab === 'intent' ? (
          <div>
            <h3 className="text-lg font-medium mb-2">Current Project Intent</h3>
            <div className={`p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <ArtifactoryMarkdown content={displayIntent} />
            </div>
            {originalIntent && originalIntent !== intent && (
              <div className="mt-4">
                <h4 className="text-md font-medium mb-2">Original Intent</h4>
                <div className={`p-3 rounded-md ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'}`}>
                  <ArtifactoryMarkdown content={originalIntent} />
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'history' ? (
          <div>
            <h3 className="text-lg font-medium mb-2">Intent Evolution History</h3>
            {intentHistory.length > 0 ? (
              <div className="space-y-4">
                {intentHistory.map((entry, index) => (
                  <div 
                    key={entry.id} 
                    className={`p-3 rounded-md ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    } border-l-4 ${
                      darkMode ? 'border-blue-600' : 'border-blue-500'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <span className="font-medium">Version {intentHistory.length - index}</span>
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <ArtifactoryMarkdown content={entry.content} size="small" className="mt-2" />
                    {entry.mutation_reason && (
                      <div className="mt-2">
                        <span className="text-xs font-medium">Reason for change:</span>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {entry.mutation_reason}
                        </p>
                      </div>
                    )}
                    {entry.affected_nodes && entry.affected_nodes.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium">Affected files:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {entry.affected_nodes.map(nodeId => {
                            // Look up the actual file path from the manifest
                            const nodePath = manifest?.nodes[nodeId]?.path || nodeId;
                            return (
                              <span 
                                key={nodeId} 
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {nodePath}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <p className="text-center">No intent history available yet.</p>
              </div>
            )}
          </div>
        ) : activeTab === 'hydration' ? (
          <div>
            <h3 className="text-lg font-medium mb-2">Project Hydration</h3>
            <HydrationPlanPanel
              manifestId={manifestId}
              sessionId={sessionId}
              maxHeight="200px"
            />
            <div className="mt-2 text-center">
              <button
                onClick={() => navigate(`/artifactory/${sessionId}/hydration-plan/${manifestId}`)}
                className="text-blue-500 hover:underline"
              >
                View Full Hydration Plan
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-medium mb-3">Project Details</h3>
            <div className="flex justify-end gap-2 mb-4">
              <ExportButton sessionId={sessionId} manifestId={manifestId} />
              <HandoffButton manifestId={manifestId} />
            </div>
            <div className="space-y-2">
              {/* Add project name field */}
              <div className="flex items-start">
                <div className={`w-32 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Project Name:</div>
                <div className="font-semibold">
                  {projectName}
                  
                  {/* Show project name history if available */}
                  {projectNameHistory.length > 1 && (
                    <div className="mt-2">
                      <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Previous names:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {projectNameHistory.slice(1).map((name, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 text-xs rounded-full ${
                              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-start">
                <div className={`w-32 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Session ID:</div>
                <div className="font-mono">{sessionId || 'N/A'}</div>
              </div>
              <div className="flex items-start">
                <div className={`w-32 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manifest ID:</div>
                <div className="font-mono">{manifestId || 'N/A'}</div>
              </div>
              <div className="flex items-start">
                <div className={`w-32 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <FiClock className="inline mr-1" />Created:
                </div>
                <div>{formatDate(createdAt)}</div>
              </div>
              <div className="flex items-start">
                <div className={`w-32 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <FiClock className="inline mr-1" />Updated:
                </div>
                <div>{formatDate(updatedAt)}</div>
              </div>
              {tags.length > 0 && (
                <div className="flex items-start">
                  <div className={`w-32 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <FiTag className="inline mr-1" />Tags:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag, index) => (
                      <span 
                        key={index} 
                        className={`px-2 py-1 text-xs rounded-full ${
                          darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
